/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";
import vector2 from "./assets/vector2.svg";
import vector3 from "./assets/vector3.svg";
import { MdWorkOutline } from "react-icons/md";
import { GrLanguage } from "react-icons/gr";
import { MdOpenInNew } from "react-icons/md";
import { twMerge } from "tailwind-merge";
import { FiAtSign, FiTwitter, FiYoutube } from "react-icons/fi";
import { FaLinkedinIn, FaPhone, FaWhatsapp } from "react-icons/fa6";
import verifiedLogo from "./assets/verified.gif";
import mixpanel from "mixpanel-browser";
import { RiInstagramFill } from "react-icons/ri";
import { FaFacebook, FaGlobe, FaPlus, FaTelegramPlane } from "react-icons/fa";
import { Link } from "react-router-dom";
import { IoIosClose } from "react-icons/io";
import { Button } from "@/shadcnComponents/ui/button";
import ReactDOM from "react-dom";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shadcnComponents/ui/tooltip";
import {
  Heart,
  InfoIcon,
  MessageCircle,
  Repeat2,
  SquareArrowOutUpRight,
  ThumbsUp,
} from "lucide-react";
import { formatFollowersCount } from "@/Helpers/format-helper";
import {
  ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
} from "@/shadcnComponents/ui/chart";
import { Bar, BarChart, CartesianGrid } from "recharts";
import { Separator } from "@radix-ui/react-select";
import { getRandomProfileEmoji, OpenInNewWindow } from "@/Helpers/helper";
import { ScrollArea, ScrollBar } from "@/shadcnComponents/ui/scroll-area";
import { Tooltip as RechartsTooltip } from "recharts";
import { TbBrandLinktree } from "react-icons/tb";

const host = import.meta.env.VITE_BACKEND_SERVER_URL;

// function extractDomain(url) {
//   try {
//     const urlObj = new URL(url);
//     return urlObj.hostname.startsWith("www.")
//       ? urlObj.hostname
//       : "www." + urlObj.hostname;
//   } catch (e) {
//     // console.error("Invalid Url: ", url);
//     return null;
//   }
// }

// Function to convert the url to a valid url
export const convertToUrl = (url) => {
  // If the url is empty, return empty string
  if (!url) {
    return "";
  }

  // Trim the url
  url = url?.trim();

  // If the url does not start with http:// or https://, add https://
  if (!/^https?:\/\//i.test(url)) {
    if (!/^www\./i.test(url)) {
      url = "www." + url;
    }
    url = "https://" + url;
  }

  // Return the url
  return url;
};

interface IconButtonProps {
  onClick?: () => void;
  icon;
  color?: string;
  ariaLabel?: string;
}

export const IconButton1: React.FC<IconButtonProps> = ({
  icon,
  onClick,
  color,
  ariaLabel,
}) => {
  return (
    <button
      className={twMerge(
        `w-[36px] h-[36px] flex-shrink-0 grid place-content-center bg-[#DC2627]/20 rounded-full p-[6px]`,
        `${color}`
      )}
      onClick={onClick}
      aria-label={ariaLabel}
    >
      {icon}
    </button>
  );
};

export const VerticalDividerVector = () => {
  return (
    <div className="flex justify-center items-center py-[12px]">
      <img
        src={vector2}
        alt="vector"
        className="w-2 h-[38px] stroke-[1px] stroke-[rgba(226,_232,_240,_0.00)]"
      />
    </div>
  );
};

export const HorizontalDividerVector = ({ width = "w-[174px]" }) => {
  return (
    <div className="flex justify-center items-center py-[12px]">
      <img
        src={vector3}
        alt="vector"
        className={twMerge(
          `h-2 stroke-[1px] stroke-[rgba(226,_232,_240,_0.00)]`,
          `${width}`
        )}
      />
    </div>
  );
};

interface SectionCardProps {
  children: React.ReactNode;
  title?: string;
  subtitle?: string;
  icon?;
  parentClass?: string;
}

export const SectionCard: React.FC<SectionCardProps> = ({
  children,
  icon,
  title,
  subtitle,
  parentClass,
}) => {
  return (
    <div
      className={cn(
        "w-full flex p-[16px] flex-col items-start gap-[20px] rounded-[8px] border-[1px] border-solid border-[#262626]",
        parentClass
      )}
    >
      <div className="flex flex-col items-start gap-[8px] self-stretch">
        <div className="flex items-start gap-[8px] self-stretch">
          {icon && (
            <span className="text-[#FFF] text-center font-[Inter] text-[24px] not-italic font-normal leading-[20px]">
              {icon}
            </span>
          )}
          <h3 className="text-[#FFF] text-center font-[Inter] text-[14px] not-italic font-semibold leading-[20px]">
            {title}
          </h3>
        </div>
        <span className="self-stretch text-[#919191] font-[Inter] text-[12px] not-italic font-normal leading-[20px]">
          {subtitle}
        </span>
      </div>
      {children}
    </div>
  );
};

interface CollaborationPortfolioCardProps {
  companyName?: string;
  companyProfile?: string;
  companyUrl?: string;
  goals?: string[];
  postLink?: string;
  status?: number;
  isBlur?: boolean;
  onKudos?: () => void;
  isKudosGiven?: boolean;
  kudosCount?: string | number;
  loading?: boolean;
}

export const CollaborationPortfolioCard: React.FC<
  CollaborationPortfolioCardProps
> = ({
  companyName,
  companyUrl,
  goals,
  postLink,
  status,
  isBlur,
  onKudos,
  isKudosGiven,
  kudosCount,
  loading,
}) => {
  return (
    <div
      className={`flex px-[16px] py-[12px] flex-col gap-[20px] self-stretch rounded-[6px] bg-[#1B1F23] w-full md:w-[271px] justify-between ${
        isBlur && "filter blur-sm"
      }`}
    >
      <div className="flex flex-col gap-[20px]">
        <div className="flex justify-between items-start self-stretch">
          <div className="flex flex-col justify-center items-start gap-[8px] overflow-hidden text-ellipsis whitespace-nowrap">
            <div className="flex flex-col justify-center items-start gap-[8px]">
              <div className="flex items-center gap-[6px]">
                <div className="flex items-center justify-center w-[16px] h-[16px]">
                  <MdWorkOutline color="#5A5A5A" size={16} />
                </div>
                <span className="text-[#E2E8F0] font-[Inter] text-[14px] not-italic font-medium leading-[normal]">
                  {companyName}
                </span>
              </div>
              {companyUrl?.trim().length > 0 && companyUrl !== null && (
                <>
                  <div className="flex items-center gap-[6px]">
                    <div className=" flex items-center justify-center w-[16px] h-[16px]">
                      <GrLanguage color="#5A5A5A" size={14} />
                    </div>
                    <button
                      className="text-[#06F] font-[Inter] text-[12px] not-italic font-normal leading-[normal]"
                      onClick={() => {
                        mixpanel.track(
                          `Clicked companyUrl on profile on Collab`,
                          { companyName: companyName, companyUrl: companyUrl }
                        );
                        window.open(convertToUrl(companyUrl), "_blank");
                      }}
                      disabled={isBlur}
                    >
                      {companyUrl.replace(/^https?:\/\//i, "").slice(0, 15) +
                        "..."}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>

          {status === 1 && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <div className="flex items-center gap-[4px] cursor-pointer">
                    <div className="w-[16px] h-[16px]">
                      <img src={verifiedLogo} alt="verified" />
                    </div>
                    <span className="text-[#E2E8F0] font-[Inter] text-[12px] not-italic font-semibold leading-[normal]">
                      Verified
                    </span>
                  </div>
                </TooltipTrigger>
                <TooltipContent className="bg-black text-white border-none">
                  <p className="font-inter text-xs">
                    This collaboration is verified by the company
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
        {goals?.length > 0 && (
          <>
            <div className="flex flex-col justify-center items-start gap-[8px]">
              <div className="flex items-center gap-[6px]">
                <span className="text-[#E2E8F0] font-[Inter] text-[16px] not-italic font-normal leading-[normal]">
                  🎯
                </span>
                <span className="text-[#E2E8F0] font-[Inter] text-[12px] not-italic font-normal leading-[normal]">
                  Collab Goals
                </span>
              </div>
              <div className="flex items-start gap-[8px] flex-wrap">
                {goals.map((goal, index) => {
                  return <TagChip key={index} title={goal} />;
                })}
              </div>
            </div>
          </>
        )}
      </div>
      {postLink?.trim().length > 0 && postLink !== null && (
        <>
          <div className="flex justify-between items-center">
            <div className="flex gap-1 items-center">
              <Heart
                size={14}
                color="#DC2627"
                className={cn(
                  "hover:fill-[#DC2627] cursor-pointer",
                  isKudosGiven ? "fill-[#DC2627]" : "fill-transparent"
                )}
                onClick={() => {
                  if (!loading || !isBlur) {
                    onKudos();
                  }
                }}
              />
              {kudosCount && kudosCount !== "0" && (
                <span className="text-[#A3A3A3] font-[Inter] text-[10px] not-italic font-medium leading-[normal]">
                  {formatFollowersCount(kudosCount)}
                </span>
              )}
            </div>
            <button
              className="flex px-[8px] py-[6px] justify-center items-center gap-[4px] text-[#DC2627] font-[Inter] text-[12px] not-italic font-medium leading-[normal]"
              onClick={() => {
                mixpanel.track(`Clicked Proof on Collaboration History`, {
                  companyName: companyName,
                  postLink: postLink,
                });
                window.open(convertToUrl(postLink), "_blank");
              }}
              disabled={isBlur}
            >
              <span>Proof</span>
              <MdOpenInNew size={14} color="#DC2627" />
            </button>
          </div>
        </>
      )}
    </div>
  );
};

interface MonetizationAvenueCardProps {
  platformName: string;
  profileLink: string;
  paidAudience: number;
  methods: string[];
  platformLogo?: string;
  isBlur?: boolean;
}

export const MonetizationAvenueCard: React.FC<MonetizationAvenueCardProps> = ({
  platformName,
  profileLink,
  paidAudience,
  methods,
  platformLogo,
  isBlur,
}) => {
  return (
    <div
      className={`flex px-[16px] py-[12px] flex-col gap-[20px] self-stretch rounded-[6px] bg-[rgba(38,_38,_38,_0.44)] w-full md:w-[271px] justify-between ${
        isBlur && "filter blur-sm"
      }`}
    >
      <div className="flex flex-col gap-[20px]">
        {/* Head */}
        <div className="flex items-center gap-[8px] self-stretch overflow-hidden text-ellipsis whitespace-nowrap">
          <div className="w-[40px] h-[40px] bg-white rounded-full flex justify-center items-center shrink-0">
            {platformLogo ? (
              <img
                src={platformLogo}
                alt={`${platformName} logo`}
                className="w-full h-full object-cover rounded-full"
              />
            ) : (
              "🥭"
            )}
          </div>
          <div className="flex flex-col justify-center items-start gap-[8px]">
            <span className="font-[Inter] text-[14px] not-italic font-semibold leading-[normal]">
              {platformName}
            </span>
            {profileLink?.trim().length > 0 && profileLink !== null && (
              <button
                className="text-[#06F] font-[Inter] text-[12px] not-italic font-normal leading-[normal]"
                onClick={() => {
                  mixpanel.track(
                    `Clicked profileLink on profile Monetization Channels`,
                    { platformName: platformName, profileLink: profileLink }
                  );
                  window.open(convertToUrl(profileLink), "_blank");
                }}
                disabled={isBlur}
              >
                {profileLink.replace(/^https?:\/\//i, "").slice(0, 15) + "..."}
              </button>
            )}
          </div>
        </div>

        {/* Body */}
        {methods?.length > 0 && (
          <>
            <div className="flex flex-col justify-center items-start gap-[8px] self-stretch">
              <span className="text-[#E2E8F0] font-[Inter] text-[12px] not-italic font-normal leading-[normal]">
                Services
              </span>
              <div className="flex items-start gap-[8px] flex-wrap">
                {methods.map((method, index) => {
                  return <TagChip key={index} title={method} />;
                })}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Footer */}
      {paidAudience >= 1 && (
        <>
          <div className="flex justify-end">
            <div className="flex px-[12px] py-[6px] items-center gap-[4px] rounded-[2px] border-[1px] border-solid border-[#FFB042] bg-[rgba(255,_176,_66,_0.12)]">
              <span>🔥</span>
              <span className="text-[#FFF9F4] font-[Inter] text-[12px] not-italic font-normal leading-[normal]">
                {paidAudience}+ Purchased
              </span>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

interface TagChipProps {
  title: string;
  icon?: string;
}

export const TagChip: React.FC<TagChipProps> = ({ title, icon }) => {
  return (
    <div className="flex px-[10px] py-[8px] justify-center items-center gap-[8px] rounded-[6px] border-[1px] border-solid border-[#262626] bg-[#292f35] text-[#A3A3A3] font-[Inter] text-[12px] not-italic font-normal leading-[normal]">
      {icon && (
        <img
          src={icon}
          alt={title}
          className="w-[16px] h-[16px] fill-[#A3A3A3]"
        />
      )}
      <span className="text-[#A3A3A3] font-[Inter] text-[14px] not-italic font-medium leading-[normal]">
        {title}
      </span>
    </div>
  );
};

interface AudienceAvenuesCardProps {
  platform: string;
  audience: number;
  link?: string;
  icon: string;
  isBlur?: boolean;
}

export const AudienceAvenuesCard: React.FC<AudienceAvenuesCardProps> = ({
  platform,
  audience,
  icon,
  link,
  isBlur,
}) => {
  const cardColor = {
    whatsapp: "border-[#60D669] bg-[rgba(96,_214,_105,_0.08)]",
    telegram: "border-[#2BACEF] bg-[rgba(43,_172,_239,_0.08)]",
    instagram: "border-[#E5476D] bg-[rgba(229,_71,_109,_0.08)]",
    linkedin: "border-[#0076B2] bg-[rgba(0,_118,_178,_0.08)]",
    youtube: "border-[#F00] bg-[rgba(255,_0,_0,_0.08)]",
  };

  let colorClass = "";

  Object.keys(cardColor).forEach((key) => {
    if (platform?.toLowerCase().includes(key)) {
      colorClass = cardColor[key];
    }
  });

  return (
    <Link
      to={link}
      target="_blank"
      className={twMerge(
        "md:max-w-[175px] flex px-[20px] py-[16px] flex-col items-center gap-[20px] flex-[1_0_0] rounded-[4px] border-[1px] border-solid border-[#60D669] bg-[rgba(96,_214,_105,_0.08)]",
        colorClass,
        isBlur && "filter blur-sm cursor-not-allowed pointer-events-none"
      )}
      onClick={() => {
        mixpanel.track(
          `Clicked ${platform} on public profile Platform Presence`,
          { platform: platform, audience: audience }
        );
      }}
      aria-disabled={isBlur}
    >
      <img src={icon} alt={platform} className="w-[28px] h-[28px]" />
      <div className="flex flex-col items-center gap-[2px]">
        <span className="text-[#FFF] text-center font-[Inter] text-[20px] not-italic font-normal leading-[normal]">
          {audience}
        </span>
        <span className="text-[#919191] text-center font-[Inter] text-[12px] not-italic font-normal leading-[normal]">
          Users
        </span>
      </div>
    </Link>
  );
};

export const UnlockForCollaborationCard = ({
  onUnlockClick,
  onCollabClick,
}) => {
  // const handleToastUnlock = () => {
  //     toast.dark(<>
  //         <div className="flex flex-col justify-center items-center gap-2 text-center">
  //             <div>
  //                 This information is locked. Do you want to unlock the information?
  //             </div>
  //             <button className="flex px-[10px] py-[6px] justify-center items-center gap-[10px] flex-[1_0_0] rounded-[6px] bg-[#DC2627] text-xs"
  //                 onClick={() => onButtonClick()}>
  //                 Yes I would like to unlock
  //             </button>
  //         </div>
  //     </>, {
  //         closeOnClick: true,
  //         pauseOnHover: true,
  //     })
  // }

  return (
    <div className="flex p-[20px] flex-col items-center gap-[16px] rounded-[8px] bg-[rgba(255,_255,_255,_0.12)] [box-shadow:0px_10px_20px_0px_rgba(0,_0,_0,_0.10)]">
      <div className="flex flex-col items-center gap-[8px] self-stretch">
        <span className="text-[#FFF] text-center font-[Inter] text-[16px] not-italic font-semibold leading-[18px]">
          Ready to collaborate?
        </span>
        <span className="text-[#9093AA] text-center font-[Inter] text-[12px] not-italic font-normal leading-[18px]">
          Let's give your brand a boost with some creative ways & authentic
          marketing!
        </span>
      </div>
      <div
        className="flex p-[16px] justify-between items-center rounded-[8px] bg-[rgba(226,_232,_240,_0.12)] w-full cursor-pointer"
        onClick={() => onUnlockClick()}
      >
        <div className="flex flex-col items-start gap-[12px] filter blur-[3px]">
          <div className="flex items-center gap-[8px]">
            <FiAtSign size={16} color="#A7CAFF" />
            <span className="text-[#FFF] text-center font-[Inter] text-[14px] not-italic font-normal leading-[normal]">
              dummy@mail.com
            </span>
          </div>
          <div className="flex items-center gap-[8px]">
            <FaPhone size={16} color="#A7CAFF" />
            <span className="text-[#FFF] text-center font-[Inter] text-[14px] not-italic font-normal leading-[normal]">
              +91 9999999999
            </span>
          </div>
        </div>
        <div className="text-[32px]">🔒</div>
      </div>
      <button
        className="flex px-[16px] py-[8px] justify-center items-center gap-[10px] flex-[1_0_0] rounded-[6px] bg-[#DC2627] self-stretch hover:-translate-y-1 hover:scale-105 duration-300 transition ease-in-out delay-150"
        onClick={() => {
          mixpanel.track("Let's Talk! clicked on Creator Public Profile");
          onCollabClick();
        }}
      >
        <span>🫱🏼‍🫲🏽</span>
        <span className="text-[#FFF] text-[14px] not-italic font-medium leading-[24px] font-[Inter]">
          Let's Talk!
        </span>
      </button>
    </div>
  );
};

interface ProfileLinkButtonSelectorProps {
  profileData: any;
  color?: string;
}

export const ProfileButtonSelector: React.FC<
  ProfileLinkButtonSelectorProps
> = ({ profileData, color }) => {
  console.log("profileData", profileData);
  return (
    <>
      {/* Button for linkedin */}
      {profileData?.linkedinLink?.length > 0 && (
        <IconButton1
          icon={<FaLinkedinIn />}
          onClick={() => {
            if (!/^https?:\/\//i.test(profileData?.linkedinLink)) {
              const Link = "https://" + profileData?.linkedinLink;
              return window.open(Link, "_blank");
            }
            window.open(profileData?.linkedinLink);
            mixpanel.track(`Clicked linkedinLink on profile on Collab`);
          }}
          color={color}
        />
      )}

      {/* Button for instaLink */}
      {profileData?.instaLink?.length > 0 && (
        <IconButton1
          icon={<RiInstagramFill />}
          onClick={() => {
            if (!/^https?:\/\//i.test(profileData?.instaLink)) {
              const Link = "https://" + profileData?.instaLink;
              return window.open(Link, "_blank");
            }
            window.open(profileData?.instaLink);
            mixpanel.track(`Clicked instaLink on profile on Collab`);
          }}
          color={color}
        />
      )}

      {/* Button for telegramLink */}
      {profileData?.telegramLink?.length > 0 && (
        <IconButton1
          icon={<FaTelegramPlane />}
          onClick={() => {
            if (!/^https?:\/\//i.test(profileData?.telegramLink)) {
              const Link = "https://" + profileData?.telegramLink;
              return window.open(Link, "_blank");
            }
            window.open(profileData?.telegramLink);
            mixpanel.track(`Clicked telegramLink on profile on Collab`);
          }}
          color={color}
        />
      )}

      {/* Button for fbLink */}
      {profileData?.fbLink?.length > 0 && (
        <IconButton1
          icon={<FaFacebook />}
          onClick={() => {
            if (!/^https?:\/\//i.test(profileData?.fbLink)) {
              const Link = "https://" + profileData?.fbLink;
              return window.open(Link, "_blank");
            }
            window.open(profileData?.fbLink);
            mixpanel.track(`Clicked fbLink on profile on Collab`);
          }}
          color={color}
        />
      )}

      {/* Button for linkTreeLink */}
      {profileData?.linkTreeLink?.length > 0 && (
        <IconButton1
          icon={<TbBrandLinktree />}
          onClick={() => {
            if (!/^https?:\/\//i.test(profileData?.linkTreeLink)) {
              const Link = "https://" + profileData?.linkTreeLink;
              return window.open(Link, "_blank");
            }
            window.open(profileData?.linkTreeLink);
            mixpanel.track(`Clicked fbLink on profile on Collab`);
          }}
          color={color}
        />
      )}

      {/* Button for whatsappLink */}
      {profileData?.whatsappLink?.length > 0 && (
        <IconButton1
          icon={<FaWhatsapp />}
          onClick={() => {
            if (!/^https?:\/\//i.test(profileData?.whatsappLink)) {
              const Link = "https://" + profileData?.whatsappLink;
              return window.open(Link, "_blank");
            }
            window.open(profileData?.whatsappLink);
            mixpanel.track(`Clicked fbLink on profile on Collab`);
          }}
          color={color}
        />
      )}

      {/* Button for YouTubeLink */}
      {profileData?.YouTubeLink?.length > 0 && (
        <IconButton1
          icon={<FiYoutube />}
          onClick={() => {
            if (!/^https?:\/\//i.test(profileData?.YouTubeLink)) {
              const Link = "https://" + profileData?.YouTubeLink;
              return window.open(Link, "_blank");
            }
            window.open(profileData?.YouTubeLink);
            mixpanel.track(`Clicked fbLink on profile on Collab`);
          }}
          color={color}
        />
      )}

      {/* Button for twitterLink */}
      {profileData?.twitterLink?.length > 0 && (
        <IconButton1
          icon={<FiTwitter />}
          onClick={() => {
            if (!/^https?:\/\//i.test(profileData?.twitterLink)) {
              const Link = "https://" + profileData?.twitterLink;
              return window.open(Link, "_blank");
            }
            window.open(profileData?.twitterLink);
            mixpanel.track(`Clicked fbLink on profile on Collab`);
          }}
          color={color}
        />
      )}

      {/* Button for websiteLink */}
      {profileData?.websiteLink?.length > 0 && (
        <IconButton1
          icon={<FaGlobe />}
          onClick={() => {
            if (!/^https?:\/\//i.test(profileData?.websiteLink)) {
              const Link = "https://" + profileData?.websiteLink;
              return window.open(Link, "_blank");
            }
            window.open(profileData?.websiteLink);
            mixpanel.track(`Clicked websiteLink on profile on Collab`);
          }}
          color={color}
        />
      )}
    </>
  );
};

interface PopupCardProps {
  children: React.ReactNode;
  slug?: string;
  className?: string;
  mainClassName?: string;
  onClose?: () => void;
}

export const PopupCard: React.FC<PopupCardProps> = ({
  onClose,
  children,
  className,
  mainClassName,
}) => {
  return (
    <>
      {ReactDOM.createPortal(
        <div className="w-screen h-screen fixed top-0 left-0 z-[10] flex items-center justify-center bg-[#12121280] filter backdrop-blur">
          <div
            className={cn(
              "w-3/4 md:w-4/12 h-fit text-white flex justify-center items-center font-inter",
              mainClassName
            )}
          >
            <div
              className={cn(
                "flex w-[296px] md:w-[400px] p-[20px] flex-col items-center rounded-[8px] bg-[#303035] [box-shadow:0px_10px_20px_0px_rgba(0,_0,_0,_0.10)]",
                className
              )}
            >
              {onClose && (
                <div className="w-full flex justify-end">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="hover:bg-inherit"
                    onClick={() => onClose()}
                  >
                    <IoIosClose
                      className="invert"
                      color={"#ffffff"}
                      size={24}
                    />
                  </Button>
                </div>
              )}

              {children}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const likes = payload.find((item) => item.dataKey === "likes");
    const comments = payload.find((item) => item.dataKey === "comments");

    return (
      <div
        tabIndex={-1}
        className="recharts-tooltip-wrapper recharts-tooltip-wrapper-right recharts-tooltip-wrapper-bottom grid min-w-[8rem] items-start gap-1.5 rounded-lg border border-border/50 bg-background px-2.5 py-1.5 text-xs shadow-xl"
        style={{ visibility: "visible", pointerEvents: "none" }}
      >
        <div className="grid gap-1.5">
          {likes && (
            <div className="flex w-full flex-wrap gap-2 items-center">
              <div
                className="shrink-0 rounded-[2px] border-[--color-border] bg-[--color-bg] h-2.5 w-2.5"
                style={{
                  "--color-bg": "var(--color-likes)",
                  "--color-border": "var(--color-likes)",
                }}
              ></div>
              <div className="flex flex-1 justify-between leading-none items-center">
                <div className="grid gap-1.5">
                  <span className="text-muted-foreground">Likes</span>
                </div>
                <span className="font-mono font-medium tabular-nums text-foreground">
                  {likes.payload.originalLikes}
                </span>
              </div>
            </div>
          )}
          {comments && (
            <div className="flex w-full flex-wrap gap-2 items-center">
              <div
                className="shrink-0 rounded-[2px] border-[--color-border] bg-[--color-bg] h-2.5 w-2.5"
                style={{
                  "--color-bg": "var(--color-comments)",
                  "--color-border": "var(--color-comments)",
                }}
              ></div>
              <div className="flex flex-1 justify-between leading-none items-center">
                <div className="grid gap-1.5">
                  <span className="text-muted-foreground">Comments</span>
                </div>
                <span className="font-mono font-medium tabular-nums text-foreground">
                  {comments.payload.originalComments}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
  return null;
};

export const InfluDetailsChart = ({
  firstThirtyLikes,
  firstThirtyComments,
}) => {
  const chartData = firstThirtyLikes?.map((item, index) => {
    const originalLikes = parseInt(item);
    const originalComments = parseInt(firstThirtyComments?.[index] || 0);
    const likesLog = Math.log10(originalLikes + 1);
    const commentsLog = Math.log10(originalComments + 1);

    return {
      month: `post ${index + 1}`,
      likes: likesLog,
      comments: commentsLog,
      originalLikes,
      originalComments,
    };
  });

  const chartConfig = {
    comments: {
      label: "Comments",
      color: "hsl(347 77% 50%)",
    },
    likes: {
      label: "Likes",
      color: "hsl(352 83% 91%)",
    },
  } satisfies ChartConfig;

  return (
    <>
      <div className="w-full">
        <SectionCard
          icon={"💭"}
          title="Engagement Over Time"
          subtitle="Visualization of how my engagement on posts has evolved "
          children={
            <div className="w-full items-center justify-center flex flex-col gap-2">
              <ChartContainer
                className="m-auto w-full md:w-3/4"
                config={chartConfig}
              >
                <BarChart accessibilityLayer data={chartData}>
                  <CartesianGrid vertical={false} horizontal={false} />
                  <RechartsTooltip content={<CustomTooltip />} cursor={false} />
                  <ChartLegend content={<ChartLegendContent />} />
                  <Bar
                    width={60}
                    dataKey="likes"
                    stackId="a"
                    fill="var(--color-likes)"
                    radius={[0, 0, 4, 4]}
                  />
                  <Bar
                    width={60}
                    dataKey="comments"
                    stackId="a"
                    fill="var(--color-comments)"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ChartContainer>
              <Separator
                color="white"
                className="w-full md:w-3/4 bg-white text-white "
              />
              <div className="w-full md:w-3/4 flex items-center justify-between">
                <span className=" text-xs font-medium leading-[14.52px] text-center text-[#A3A3A3]">
                  Latest
                </span>
                <span className=" text-xs font-medium leading-[14.52px] text-center text-[#A3A3A3]">
                  Oldest
                </span>
              </div>
            </div>
          }
        />
      </div>
    </>
  );
};

interface TopPostCardProps {
  profile: string;
  name: string;
  postDescription: string;
  likes?: number | string;
  comments?: number | string;
  reposts?: number | string;
  postLink?: string;
  tagline?: string;
  className?: string;
}

export const TopPostCard = ({
  profile,
  name,
  postDescription,
  likes,
  comments,
  reposts,
  postLink,
  tagline,
  className,
}: TopPostCardProps) => {
  return (
    <>
      <div
        className={cn(
          "w-[270px] p-4 flex flex-col bg-[#1B1F23]  gap-[20px] rounded-[10px] justify-between",
          className
        )}
      >
        <div className="flex flex-col gap-[20px]">
          <div className="w-full items-center justify-start flex gap-2 ">
            <div className="overflow-hidden min-w-[40px] min-h-[40px]  rounded-full  ">
              <img
                className="w-[40px] h-[40px] cover "
                src={profile}
                alt=""
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                onError={(e: any) => {
                  const randomProfileEmoji = getRandomProfileEmoji();
                  e.target.onerror = null;
                  e.target.src = randomProfileEmoji;
                }}
              />
            </div>
            <div className="w-full flex flex-col gap-1">
              <span className=" text-xs font-semibold leading-[14.52px] text-left">
                {name}
              </span>
              <span className="text-xs text-[#949698] font-inter font-light text-left line-clamp-1 ">
                {tagline}
              </span>
            </div>
          </div>
          <div className="flex flex-wrap flex-col gap-1">
            <div className="text-[#C7C3C3] font-[Inter] text-[12px] not-italic font-normal leading-[22px] break-all line-clamp-[9]">
              {postDescription}
            </div>
            <button
              onClick={() => OpenInNewWindow({ link: postLink })}
              className="cursor-pointer text-xs font-bold leading-[14.52px] text-left text-[#7f7f7f]  underline line-clamp-4 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-sm p-1 -m-1 transition-colors duration-200"
              aria-label={`View more details about: ${postDescription?.substring(
                0,
                50
              )}...`}
              type="button"
            >
              view more
            </button>
          </div>
        </div>

        <div className=" flex gap-[20px] ">
          {[
            {
              icon: <ThumbsUp size={20} color="#AFB1B2" />,
              key: "Avg. Engagement",
              value: `${likes ?? "NA"}`,
            },
            {
              icon: <MessageCircle size={20} color="#AFB1B2" />,
              key: "Avg. Likes",
              value: `${comments ?? "NA"}`,
            },
            {
              icon: <Repeat2 size={20} color="#AFB1B2" />,
              key: "Avg. Comments",
              value: `${reposts ?? "NA"}`,
            },
          ]?.map((item, i) => {
            if (item?.value === "NA" || parseInt(item?.value) <= 0) return null;
            return (
              <div
                key={i}
                className="flex gap-1 items-center  @apply text-[12px] font-normal leading-[14.52px] text-left text-[#AFB1B2]"
              >
                {item.icon}
                {item.value}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};

export const InfluencerTopPosts = ({ TopPosts, influencer }) => {
  return (
    <SectionCard
      title="Most Engaged Posts"
      subtitle="My Top 3 posts with the highest engagement"
      icon={"💭"}
    >
      <div className=" w-full overflow-hidden">
        <ScrollArea>
          <div className="flex gap-4  pb-2">
            {TopPosts?.map((post, index) => (
              <TopPostCard
                key={index}
                profile={influencer?.profile}
                name={influencer?.name}
                postDescription={post?.postDescription}
                likes={post?.likes}
                comments={post?.comments}
                reposts={post?.reposts}
                postLink={post?.postFeedUrl}
                tagline={influencer?.tagline}
              />
            ))}
          </div>
          <ScrollBar orientation="horizontal" />
        </ScrollArea>
      </div>
    </SectionCard>
  );
};

interface InfoCardContainerProps {
  title: string;
  description?: string;
  icon?: React.ReactNode | string;
  actionArea?: React.ReactNode;
}

export const InfoCardContainer: React.FC<InfoCardContainerProps> = ({
  actionArea,
  icon,
  title,
  description,
}) => {
  return (
    <div className="w-full flex p-[16px] flex-col items-start gap-[20px] rounded-[8px] border-[1px] border-solid border-[#262626]">
      <div className="w-full flex flex-col md:flex-row text-center items-center md:text-start gap-5 justify-between">
        <div className="flex flex-col gap-[8px]">
          <div className="flex items-start justify-center md:justify-start gap-[8px] self-stretch">
            {icon}
            <span className="text-[#FFF] text-center font-[Inter] text-[14px] not-italic font-semibold leading-[20px]">
              {title}
            </span>
          </div>
          <span className="self-stretch text-[#919191] font-[Inter] text-[12px] not-italic font-normal leading-[20px]">
            {description}
          </span>
        </div>
        <div className="flex gap-5 items-center">{actionArea}</div>
      </div>
    </div>
  );
};

interface InfoCardContainerV2Props extends InfoCardContainerProps {
  bgColor?: string;
}

export const InfoCardContainerV2: React.FC<InfoCardContainerV2Props> = ({
  actionArea,
  icon,
  title,
  description,
  bgColor,
}) => {
  return (
    <div
      className={cn(
        "w-full flex p-[16px] flex-col items-start gap-[20px] rounded-[8px] border-[1px] border-solid border-[#262626]",
        bgColor && bgColor
      )}
    >
      <div className="w-full flex flex-col md:flex-row text-center items-center md:text-start gap-5 justify-between">
        <div className="flex flex-col gap-[8px]">
          <div className="flex items-start justify-center md:justify-start gap-[8px] self-stretch">
            {icon}
            <span className="text-[#FFF] text-center font-[Inter] text-[14px] not-italic font-semibold leading-[20px]">
              {title}
            </span>
          </div>
          <span className="self-stretch text-[#919191] font-[Inter] text-[12px] not-italic font-normal leading-[20px]">
            {description}
          </span>
        </div>
        <div className="flex gap-5 items-center">{actionArea}</div>
      </div>
    </div>
  );
};

export const AddDataCard = ({ action, title, description }) => {
  return (
    <>
      <div className="size-full flex flex-col justify-center items-center gap-3 flex-[1_0_0] self-stretch p-5 rounded-[10px] border-[1px] border-[#1B1F23] bg-[#1B1F23] border-dashed">
        <div
          onClick={() => action()}
          className="flex cursor-pointer justify-center items-center gap-2 p-3 border border-[color:var(--slate-200,#E2E8F0)] rounded-[96px] border-solid bg-[#fff] "
        >
          <FaPlus color="#000000" />
        </div>
        <span className="text-white text-center text-xl not-italic font-bold leading-6">
          {title}
        </span>
        <span className="text-neutral-400 text-center text-xs not-italic font-normal leading-5">
          {description}
        </span>
      </div>
    </>
  );
};

interface HighlightMetricsCardProps {
  icon?: React.ReactNode | string;
  value: string | number;
  title: string;
}

export const HighlightMetricsCard: React.FC<HighlightMetricsCardProps> = ({
  icon,
  value,
  title,
}) => {
  return (
    <div className={`w-full flex p-[22px] rounded-[10px] bg-[#1B1F23] h-full`}>
      <div className="flex items-center gap-[12px]">
        {icon && <div className="flex items-center justify-center">{icon}</div>}
        <div className="flex flex-col">
          <span className="text-[#FFF] font-[Inter] text-[20px] not-italic font-bold leading-[normal]">
            {value}
          </span>
          <span className="text-[#EEE] cursor-pointer flex items-center gap-1 justify-center font-[Inter] text-[12px] not-italic font-normal leading-[normal]">
            {title}
            {title === "Avg Eng." && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <InfoIcon size={15} />
                  </TooltipTrigger>
                  <TooltipContent className="bg-current border-none ">
                    <p className="text-white">
                      Engagement Rate is the percentage of followers engaging{" "}
                      <br />
                      with the users content.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </span>
        </div>
      </div>
    </div>
  );
};

interface TopLinksCardProps {
  link?: string;
  linkText?: string;
  icon?: string;
  index: number;
}

export const TopLinksCard: React.FC<TopLinksCardProps> = ({
  link,
  linkText,
  icon,
  index,
}) => {
  return (
    <div
      className="bg-[#1B1F23] md:w-fit w-full group rounded-[8px] border-[1px] border-solid border-[#262626] hover:border-[#DC2627] p-[1rem] md:py-[0.75rem] md:px-[1rem] flex justify-between gap-5 items-center cursor-pointer"
      onClick={() => {
        mixpanel.track("Top Link Clicked", {
          link: link,
          position: index + 1,
        });
        window.open(link, "_blank");
      }}
    >
      <div className="flex gap-2 items-center">
        <div className="rounded-full shrink-0">
          {(icon || link) && (
            <img
              src={`${host}/api/file/proxyImage?imageUrl=${encodeURIComponent(
                icon ||
                  `https://icons.duckduckgo.com/ip3/${
                    new URL(link).hostname
                  }.ico`
              )}`}
              onError={({ currentTarget }) => {
                currentTarget.onerror = null; // prevents looping
                currentTarget.src =
                  "https://cdn-icons-png.flaticon.com/512/5339/5339181.png";
              }}
              alt=""
              className="object-cover size-[26px] "
            />
          )}
        </div>
        <span className="text-white font-medium text-[14px]">{linkText}</span>
      </div>
      <SquareArrowOutUpRight
        size={16}
        className="text-white p-0 group-hover:text-[#DC2627] bg-transparent"
      />
    </div>
  );
};
